import { useMutation, useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import {
  RedRed50,
  SequenceMenu,
  Skeleton,
  useModalState,
  useNotifications,
  SimpleModal,
  Box
} from '@sequencehq/core-components'
import { match } from 'ts-pattern'
import { usePermissions } from 'lib/rbac'
import {
  amendmentLockSentence,
  useScheduleAmendmentLock
} from 'common/components/ScheduleAmendmentLock'

export const ArchiveScheduleMenuItem = (props: {
  scheduleId: string
  onSuccess?: () => void
  disabled?: boolean
  /**
   * The schedule's `lockedByQuoteId`. While it is set the API rejects the
   * archive, so the item is disabled and says which quote to go and deal with.
   */
  lockedByQuoteId?: string
  'data-testid'?: string
}) => {
  const { can } = usePermissions()
  const { isModalOpen, openModal, closeModal } = useModalState()
  const amendmentLock = useScheduleAmendmentLock(props.lockedByQuoteId)

  if (!can('org.billing:archive')) {
    return null
  }

  return (
    <>
      <SequenceMenu.Item
        uuid="archive-schedule"
        label="Archive schedule"
        isDisabled={props.disabled || amendmentLock.isLocked}
        // A menu item's tooltip is a plain string, so the quote is named here
        // rather than linked as it is elsewhere.
        tooltip={
          amendmentLock.isLocked
            ? amendmentLockSentence('archived', amendmentLock.quoteName)
            : undefined
        }
        style={{ color: RedRed50 }}
        data-testid={props['data-testid']}
        onClick={openModal}
      />
      <ArchiveScheduleModal
        scheduleId={props.scheduleId}
        isOpen={isModalOpen}
        onClose={closeModal}
        onSuccess={props.onSuccess}
      />
    </>
  )
}

const ArchiveScheduleModal = (props: {
  scheduleId: string
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}) => {
  const hook = useArchiveScheduleModal({
    scheduleId: props.scheduleId,
    onSuccess: props.onSuccess
  })

  return (
    <SimpleModal
      variant="dialog"
      data-testid="archiveScheduleModal"
      isOpen={props.isOpen}
      onClose={props.onClose}
      title="Archive schedule"
      submitButtonText="Archive"
      submittingButtonText="Archiving..."
      isDestructive
      disableSubmitOnEnter
      onSubmit={hook.archive}
    >
      {hook.isLoading || !hook.scheduleStatus ? (
        <Skeleton width="100%" height="32px" />
      ) : (
        <Box>
          {match(hook.scheduleStatus)
            .with('DRAFT', () => (
              <>This will archive the billing schedule immediately.</>
            ))
            .with('PENDING', () => (
              <>
                This will cancel and archive the billing schedule immediately.
              </>
            ))
            .with('ACTIVE', () => (
              <>
                This will cancel and archive the billing schedule immediately.
                All in progress invoices will be deleted and all draft invoices
                will be voided. Finalized and sent invoices will remain.
              </>
            ))
            .with('SUSPENDED', () => (
              <>
                This will cancel and archive the billing schedule immediately.
                All in progress invoices will be deleted and all draft invoices
                will be voided. Finalized and sent invoices will remain.
              </>
            ))
            .with('CANCELLED', () => (
              <>
                This will archive the billing schedule immediately. All draft
                invoices will be voided. Finalized and sent invoices will
                remain.
              </>
            ))
            .with('COMPLETED', () => (
              <>
                This will archive the billing schedule immediately. All draft
                invoices will be voided. Finalized and sent invoices will
                remain.
              </>
            ))
            .exhaustive()}
        </Box>
      )}
    </SimpleModal>
  )
}

const useArchiveScheduleModal = (props: {
  scheduleId: string
  onSuccess?: () => void
}) => {
  const scheduleQuery = useQuery(
    dashboard20240730Client.getBillingSchedule,
    {
      id: props.scheduleId
    },
    {
      select: data => {
        return data?.status
      }
    }
  )

  const { displayNotification } = useNotifications()

  const archiveScheduleMutator = useMutation(
    dashboard20240730Client.postBillingScheduleArchive
  )

  return {
    isLoading: scheduleQuery.isPending,
    scheduleStatus: scheduleQuery.data,
    archive: async () => {
      const res = await archiveScheduleMutator.mutateAsync({
        id: props.scheduleId
      })

      if (!res) {
        displayNotification('Could not archive schedule', { type: 'error' })
        return false
      }

      if (props.onSuccess) {
        await props.onSuccess()
      }

      displayNotification(`Schedule archived`, {
        type: 'success'
      })
      return true
    }
  }
}
